import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { SafeHtml } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { ReleaseNotesModalViewProperties } from './release-notes-modal.component';
import { catchError } from 'rxjs/operators';
import { HtmlSanitizerService } from '../services/html-sanitizer.service';

@Injectable()
export class ReleaseNotesService {
  private http = inject(HttpClient);
  private _htmlSanitizerService = inject(HtmlSanitizerService);

  private VIEWED_RELEASE_NOTES_ID_KEY = 'viewedReleaseNotesId';
  private LEGACY_VERSION_LOCAL_STORAGE_KEY = 'version';
  private LEGACY_10_7_DISCOURSE_ID = 146;
  private _baseUrl = 'https://learn.goziro.com';
  private _latestLearnId: number;

  migrateLocalStorage(): void {
    const legacyVersion = localStorage.getItem(this.LEGACY_VERSION_LOCAL_STORAGE_KEY);
    if (legacyVersion !== null) {
      localStorage.removeItem(this.LEGACY_VERSION_LOCAL_STORAGE_KEY);
      localStorage.setItem(this.VIEWED_RELEASE_NOTES_ID_KEY, String(this.LEGACY_10_7_DISCOURSE_ID));
    }
  }

  setViewedReleaseNotesId(): void {
    localStorage.setItem(this.VIEWED_RELEASE_NOTES_ID_KEY, `${this._latestLearnId}`);
  }

  getLatestReleaseNotes(): Observable<ReleaseNotesModalViewProperties | null> {
    return this._getAllReleaseNotes().pipe(
      switchMap((releaseNotes: any) => {
        const topics: any[] = releaseNotes.topic_list.topics;
        const newestTopic = topics.reduce((maxTopic: any, topic: any) => (topic.id > maxTopic.id ? topic : maxTopic), {
          id: 0,
        });

        const viewedId = localStorage.getItem(this.VIEWED_RELEASE_NOTES_ID_KEY);
        if (!viewedId || Number(viewedId) >= newestTopic.id) {
          return of(null);
        }

        this._latestLearnId = newestTopic.id;

        return this._getDocumentation(newestTopic.id).pipe(
          map((html: SafeHtml) => ({
            html,
            title: newestTopic.title,
            slug: newestTopic.slug,
          })),
          catchError(() => of(null)),
        );
      }),
      catchError(() => of(null)),
    );
  }

  private _getAllReleaseNotes(): Observable<any> {
    return this.http.get(`${this._baseUrl}/c/5/l/latest.json`);
  }

  private _getDocumentation(id: number): Observable<SafeHtml> {
    return this.http.get(`${this._baseUrl}/t/${id}.json`).pipe(
      map((response: any) => {
        const rawHtml = response?.post_stream?.posts[0]?.cooked ?? '';
        return this._htmlSanitizerService.sanitizeAndTrustHtml(rawHtml);
      }),
    );
  }
}
